import React from "react";
import { Icon, NamedProperty, Stack, Text, TooltipHost } from "sprint-common-web";

import { AssetType } from "../../../../common/types/AssetType";
import { LanguageReference } from "../../../../common/types/references/LanguageReference";
import { OrderResolutionDimensions } from "../../types/OrderResolutionDimensions";

type CloudVideoAssetInfoProps = {
    requestedAspectRatio: NamedProperty;
    requestedResolution: OrderResolutionDimensions | undefined;
    nonMatchingVideos: Array<{
        assetId: string;
        aspectRatio: NamedProperty;
        resolution: OrderResolutionDimensions;
        subtitle: LanguageReference;
        assetType?: AssetType;
    }>;
};

export const CloudVideoAssetInfo = ({
    requestedAspectRatio,
    requestedResolution,
    nonMatchingVideos
}: CloudVideoAssetInfoProps): JSX.Element => {
    const getNoMatchMessage = (): string => {
        let message = `No video asset matches requested aspect ratio ${requestedAspectRatio.name}`;

        if (!!requestedResolution?.height && !!requestedResolution?.width) {
            message += ` and resolution ${formatResolution(requestedResolution)}.`;
        }

        return message;
    };

    const renderAvailableVideos = (): React.ReactNode => {
        if (!nonMatchingVideos.length) {
            return "-";
        }

        return nonMatchingVideos.map((video) => (
            <React.Fragment key={video.assetId}>
                <br />
                <span>
                    {formatVideoInfo({
                        aspectRatio: video.aspectRatio,
                        resolution: video.resolution,
                        assetType: video.assetType
                    })}
                </span>
            </React.Fragment>
        ));
    };

    const tooltipContent = (
        <Stack>
            <Text variant="smallPlus" nowrap={false}>
                {getNoMatchMessage()}
            </Text>

            <Text variant="smallPlus" nowrap={false}>
                Available videos: {renderAvailableVideos()}
            </Text>
        </Stack>
    );

    return (
        <TooltipHost
            styles={{ root: { display: "flex", alignItems: "center", cursor: "pointer" } }}
            content={tooltipContent}
        >
            <Icon iconName="Info" />
        </TooltipHost>
    );
};

const formatResolution = (resolution: OrderResolutionDimensions): string => `${resolution.width}x${resolution.height}`;

const formatVideoInfo = ({
    aspectRatio,
    resolution,
    assetType
}: {
    aspectRatio: NamedProperty;
    resolution: OrderResolutionDimensions;
    assetType?: AssetType;
}): string => {
    let result = "";

    if (assetType) {
        if (assetType === AssetType.Cloud) {
            result += "[Cloud] ";
        }

        if (assetType === AssetType.OnPrem) {
            result += "[On-Prem] ";
        }
    }

    result += `${aspectRatio.name}${!!resolution ? ` and ${formatResolution(resolution)}` : ""}`;

    return result;
};
